import { api } from "../../api/apiSlice";

// Alarm Response (2026-09-17, TexasAlarms.Api src/Alarms, Batch177).
//
// The web side is the CONTROL ROOM's: record + dispatch alarms, watch the
// board, review the report a mobile unit sends back and act on it (set the
// cause, push to Tech, close), plus the Mobile Units master and the report.
// The car's own steps (open, arrive, photos, report) are Flutter-only and
// deliberately not exposed here, even though the endpoints exist.
const responseTags = (recordGuid) => [
  { type: "AlarmResponses", id: recordGuid },
  { type: "AlarmResponses", id: "LIST" },
  { type: "MobileUnits", id: "LIST" },
];

export const alarmsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAlarmLookups: builder.query({
      query: () => ({ url: "/alarms/lookups" }),
    }),

    // ---- Mobile units ----
    getMobileUnits: builder.query({
      query: (params) => ({ url: "/alarms/mobile-units", params }),
      providesTags: [{ type: "MobileUnits", id: "LIST" }],
    }),
    getMobileUnitDeviceUsers: builder.query({
      query: () => ({ url: "/alarms/mobile-units/device-users" }),
      providesTags: [{ type: "MobileUnits", id: "DEVICE_USERS" }],
    }),
    createMobileUnit: builder.mutation({
      query: (body) => ({ url: "/alarms/mobile-units", method: "POST", data: body }),
      invalidatesTags: [{ type: "MobileUnits", id: "LIST" }, { type: "MobileUnits", id: "DEVICE_USERS" }],
    }),
    updateMobileUnit: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/alarms/mobile-units/${recordGuid}`, method: "PUT", data: body }),
      invalidatesTags: [{ type: "MobileUnits", id: "LIST" }, { type: "MobileUnits", id: "DEVICE_USERS" }],
    }),

    // ---- Dispatch form ----
    getAlarmCustomerSites: builder.query({
      query: ({ branchId, custId }) => ({ url: "/alarms/customer-sites", params: { branchId, custId } }),
    }),

    // ---- Responses ----
    getAlarmResponses: builder.query({
      query: (params) => ({ url: "/alarms/responses", params }),
      providesTags: (result) =>
        result
          ? [...result.map((r) => ({ type: "AlarmResponses", id: r.recordGuid })), { type: "AlarmResponses", id: "LIST" }]
          : [{ type: "AlarmResponses", id: "LIST" }],
    }),
    getAlarmResponse: builder.query({
      query: (recordGuid) => ({ url: `/alarms/responses/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "AlarmResponses", id: recordGuid }],
    }),
    // Authenticated image - fetched as a Blob (axiosBaseQuery passes responseType through).
    getAlarmPhoto: builder.query({
      query: ({ recordGuid, photoGuid }) => ({
        url: `/alarms/responses/${recordGuid}/photos/${photoGuid}`,
        responseType: "blob",
      }),
      keepUnusedDataFor: 600,
    }),

    dispatchAlarm: builder.mutation({
      query: (body) => ({ url: "/alarms/responses", method: "POST", data: body }),
      invalidatesTags: [{ type: "AlarmResponses", id: "LIST" }, { type: "MobileUnits", id: "LIST" }],
    }),
    reassignAlarm: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/alarms/responses/${recordGuid}/reassign`, method: "PUT", data: body }),
      invalidatesTags: (r, e, { recordGuid }) => responseTags(recordGuid),
    }),
    cancelAlarm: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/alarms/responses/${recordGuid}/cancel`, method: "POST", data: body }),
      invalidatesTags: (r, e, { recordGuid }) => responseTags(recordGuid),
    }),
    closeAlarm: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/alarms/responses/${recordGuid}/close`, method: "POST", data: body }),
      invalidatesTags: (r, e, { recordGuid }) => responseTags(recordGuid),
    }),
    pushAlarmToTech: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/alarms/responses/${recordGuid}/push-to-tech`, method: "POST", data: body }),
      invalidatesTags: (r, e, { recordGuid }) => [
        ...responseTags(recordGuid),
        { type: "TechnicalWork", id: "LIST" },
      ],
    }),
    setAlarmCause: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/alarms/responses/${recordGuid}/cause`, method: "PUT", data: body }),
      invalidatesTags: (r, e, { recordGuid }) => responseTags(recordGuid),
    }),
  }),
});

export const {
  useGetAlarmLookupsQuery,
  useGetMobileUnitsQuery,
  useGetMobileUnitDeviceUsersQuery,
  useCreateMobileUnitMutation,
  useUpdateMobileUnitMutation,
  useGetAlarmCustomerSitesQuery,
  useGetAlarmResponsesQuery,
  useGetAlarmResponseQuery,
  useGetAlarmPhotoQuery,
  useLazyGetAlarmPhotoQuery,
  useLazyGetAlarmResponseQuery,
  useDispatchAlarmMutation,
  useReassignAlarmMutation,
  useCancelAlarmMutation,
  useCloseAlarmMutation,
  usePushAlarmToTechMutation,
  useSetAlarmCauseMutation,
} = alarmsApi;
